import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';

import { HELP_TABS } from 'xm-constants/helpView';
import { getCurrentTab, getIsHelpDrawerOpen } from 'selectors/helpView';
import useSyncedBodyClassName from 'hooks/useSyncedBodyClassName';
import ErrorBoundary from 'components/ErrorBoundary';

import { getHelpViewStyling, HelpGeometryContext } from './utils';
import {
  useAutomaticQuickStartOpening,
  useHelpGeometryContextProviderValue,
} from './hooks';

import HelpViewHeader from './HelpViewHeader';
import HelpViewResizers from './HelpViewResizers';
import HelpPage from './HelpPage';
import QuickStartGuide from './QuickStartGuide';
import styles from './HelpView.module.scss';

export default function HelpView() {
  const shouldRender = !!useSelector(getIsHelpDrawerOpen);

  const helpGeometryContextValue = useHelpGeometryContextProviderValue();
  const { isHelpDrawerDocked, geometry } = helpGeometryContextValue;

  const dockedHelpViewOpen = !!shouldRender && isHelpDrawerDocked;
  useSyncedBodyClassName(styles.dockedHelpViewOpen, dockedHelpViewOpen);
  useAutomaticQuickStartOpening();

  const currentTab = useSelector(getCurrentTab);
  const style = useMemo(
    () => (isHelpDrawerDocked ? undefined : getHelpViewStyling(geometry)),
    [geometry, isHelpDrawerDocked],
  );

  return (
    shouldRender && (
      <HelpGeometryContext.Provider value={helpGeometryContextValue}>
        <div
          className={classnames(styles.helpView, {
            [styles.docked]: isHelpDrawerDocked,
          })}
          style={style}
        >
          <div className={styles.helpViewContent}>
            <HelpViewHeader />
            <ErrorBoundary>
              {currentTab === HELP_TABS.HELP && <HelpPage />}
              {currentTab === HELP_TABS.QUICK_START && <QuickStartGuide />}
            </ErrorBoundary>
          </div>
          {!isHelpDrawerDocked && <HelpViewResizers />}
        </div>
      </HelpGeometryContext.Provider>
    )
  );
}
HelpView.displayName = 'HelpView';
